import {
  Button,
  CheckboxLineField,
  InputSelectControlField,
  TextInputField,
  GreyGrey10,
  GreyGrey60,
  IconPlus,
  Primary13SemiBold,
  Section,
  SequenceMenu,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ValidationError } from '@sequencehq/utils'
import {
  CountriesAlpha2,
  getStateOptions,
  statesForCountry
} from '@sequencehq/api/utils/commonEnums'
import { State } from '@sequencehq/api'
import { CountrySelectField } from 'SettingsV2/index'
import { TaxRegistrationEntry } from '../types'

const inputStyles = {
  paddingLeft: '12px'
}

const fieldStyles = {
  label: { ...Primary13SemiBold, color: GreyGrey60 }
}

const newEntry = (
  defaultCountry: CountriesAlpha2 | '' = '',
  defaultState?: State
): TaxRegistrationEntry => {
  const stateBelongsToCountry =
    Boolean(defaultCountry) &&
    Boolean(defaultState) &&
    statesForCountry(defaultCountry as CountriesAlpha2).includes(
      defaultState as State
    )

  return {
    id: crypto.randomUUID(),
    country: defaultCountry,
    state: stateBelongsToCountry ? defaultState : undefined,
    identifier: ''
  }
}

export const CustomerTaxRegistrationsField = (props: {
  value: Array<TaxRegistrationEntry>
  onChange: (newValue: Array<TaxRegistrationEntry>) => void
  validationErrors: Array<ValidationError>
  /** Seeds new registration rows' country; the customer's address country. */
  defaultCountry?: CountriesAlpha2 | ''
  /** Seeds new rows' state; applied only when valid for `defaultCountry`. */
  defaultState?: State
  'data-testid'?: string
}) => {
  const testId = props['data-testid']
  const hasRegistrations = props.value.length > 0

  const errorsFor = (entryId: string, field: string) =>
    props.validationErrors.filter(
      ve => ve.metadata?.entryId === entryId && ve.metadata?.field === field
    )

  const updateEntry = (id: string, patch: Partial<TaxRegistrationEntry>) =>
    props.onChange(
      props.value.map(entry =>
        entry.id === id ? { ...entry, ...patch } : entry
      )
    )

  const removeEntry = (id: string) =>
    props.onChange(props.value.filter(e => e.id !== id))

  return (
    <Flex direction="column" gap="8px">
      <CheckboxLineField
        data-testid={testId ? `${testId}.toggle` : undefined}
        text="Add tax registration"
        variant="v2"
        value={hasRegistrations}
        onChange={checked => {
          props.onChange(
            checked ? [newEntry(props.defaultCountry, props.defaultState)] : []
          )
        }}
      />
      {hasRegistrations && (
        <Flex direction="column" gap="8px">
          <Flex direction="column" gap="8px">
            {props.value.map((entry, idx) => {
              const stateOptions = entry.country
                ? getStateOptions(entry.country as CountriesAlpha2)
                : []
              const showState = stateOptions.length > 0

              return (
                <Section
                  variant="emphasized"
                  key={entry.id}
                  padding="16px"
                  position="relative"
                  display="flex"
                  flexDirection="column"
                  gap="8px"
                  borderColor={GreyGrey10}
                  data-testid={testId ? `${testId}.row.${idx}` : undefined}
                >
                  <Box position="absolute" top="8px" right="16px">
                    <SequenceMenu
                      variant="ghost"
                      data-testid={
                        testId
                          ? `${testId}.registration.${idx}.menu`
                          : undefined
                      }
                      buttonSize="small"
                      alignment="bottom-right"
                      items={[
                        ({ setIsOpen }) => (
                          <SequenceMenu.Item
                            key="Remove registration"
                            uuid="remove-registration"
                            label="Remove registration"
                            data-testid={
                              testId
                                ? `${testId}.registration.${idx}.menu.remove`
                                : undefined
                            }
                            onClick={() => {
                              removeEntry(entry.id)
                              setIsOpen?.(false)
                            }}
                            destructive
                          />
                        )
                      ]}
                    />
                  </Box>
                  {/* Top row: country + optional state */}
                  <Flex gap="8px" alignItems="flex-start">
                    <Flex flex="1">
                      <CountrySelectField
                        data-testid={
                          testId
                            ? `${testId}.registration.${idx}.country`
                            : undefined
                        }
                        label="Country"
                        value={entry.country}
                        onChange={newCountry => {
                          updateEntry(entry.id, {
                            country: newCountry as CountriesAlpha2,
                            state: undefined
                          })
                        }}
                        placeholder="Select country"
                        validationErrors={errorsFor(entry.id, 'country')}
                        styles={fieldStyles}
                      />
                    </Flex>
                    {showState && (
                      <Flex flex="1">
                        <InputSelectControlField
                          data-testid={
                            testId
                              ? `${testId}.registration.${idx}.state`
                              : undefined
                          }
                          label="State"
                          value={entry.state ?? ''}
                          onChange={newState =>
                            updateEntry(entry.id, {
                              state: newState as State
                            })
                          }
                          options={stateOptions}
                          placeholder="Select state"
                          validationErrors={errorsFor(entry.id, 'state')}
                          styles={fieldStyles}
                        />
                      </Flex>
                    )}
                  </Flex>
                  {/* Bottom row: tax ID value */}
                  <TextInputField
                    data-testid={
                      testId
                        ? `${testId}.registration.${idx}.identifier`
                        : undefined
                    }
                    label="Tax ID"
                    value={entry.identifier}
                    onChange={e =>
                      updateEntry(entry.id, { identifier: e.target.value })
                    }
                    placeholder="Tax ID"
                    validationErrors={errorsFor(entry.id, 'identifier')}
                    {...inputStyles}
                    styles={fieldStyles}
                  />
                </Section>
              )
            })}
          </Flex>
          <Button
            variant="ghost"
            fullWidth
            leadingIcon={<IconPlus />}
            data-testid={testId ? `${testId}.addRegistration` : undefined}
            onClick={() =>
              props.onChange([
                ...props.value,
                newEntry(props.defaultCountry, props.defaultState)
              ])
            }
          >
            Add registration
          </Button>
        </Flex>
      )}
    </Flex>
  )
}
